<script lang="ts">
	import type { SbImage, SbLink, SbTextColor, TextAlignment } from '../../types';
	import PageGrid from '../../components/pageGrid.svelte';
	import Image from '../../components/image.svelte';
	import SbRichtext from '../../components/sbRichtext.svelte';
	import ButtonBase from '$lib/storyblok/components/buttons/buttonBase.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';

	type ColorVariants =
		| 'red'
		| 'red-gradient'
		| 'green'
		| 'green-gradient'
		| 'brown'
		| 'brown-gradient'
		| 'blue'
		| 'blue-gradient'
		| 'white';

	export let content: ISbStoryData<{
		title: string | ISbRichtext;
		text: string | ISbRichtext;
		image?: SbImage;
		asset: ISbStoryData['content'][];
		text_align: TextAlignment;
		color_variant: ColorVariants;
		text_color: SbTextColor;
		align: 'imageRight' | 'imageLeft';
		title_visibility: 'default' | 'mobile_only' | 'desktop_only';
		title_position: 'below_image' | 'above_image';
		cta_label: string;
		cta_target: SbLink;
	}>['content'];

	$: titlePosition = content.title_position || 'below_image';

	const bgColorVariants: Record<string, string> = {
		red: 'bg-[#460E00]',
		'red-gradient': 'bg-gradient-to-b from-[#9A3F2A] to-[#B75F4B]',
		green: 'bg-[#001A10]',
		'green-gradient': 'bg-gradient-to-b from-[#194334] to-[#2D6751]',
		brown: 'bg-[#240D02]',
		'brown-gradient': 'bg-gradient-to-b from-[#7F4B26] to-[#AB7650]',
		blue: 'bg-[#081632]',
		'blue-gradient': 'bg-gradient-to-b from-[#0A1B40] to-[#1E386F]',
		white: 'bg-white'
	};

	const buttonColorVariants: Record<string, string> = {
		red: 'text-[#460E00]',
		'red-gradient': 'text-[#460E00]',
		green: 'text-[#001A10]',
		'green-gradient': 'text-[#001A10]',
		brown: 'text-[#240D02]',
		'brown-gradient': 'text-[#240D02]',
		blue: 'text-[#081632]',
		'blue-gradient': 'text-[#081632]',
		white: 'bg-white'
	};

	$: bgColor = bgColorVariants[content.color_variant];
	$: buttonTextColor = buttonColorVariants[content.color_variant];
</script>

<div use:editable={content} class="{bgColor} pb-14 pt-10 lg:py-18">
	<PageGrid gridClass="grid-flow-row-dense text-{content.text_color || 'brown-1'}">
		<div class="max-lg:col-span-full lg:hidden" class:hidden={titlePosition === 'below_image'}>
			<div
				class="text-[32px]/[110%] font-semibold tracking-[-.32px] lg:text-[48px]/[110%] lg:tracking-[-.48px]"
				class:lg:hidden={content.title_visibility === 'mobile_only'}
				class:max-lg:hidden={content.title_visibility === 'desktop_only'}
			>
				<SbRichtext text={content.title} />
			</div>
		</div>
		<div
			class="max-lg:col-span-full"
			class:lg:col-start-7={content.align === 'imageRight'}
			class:lg:col-end-13={content.align === 'imageRight'}
			class:lg:col-start-1={content.align === 'imageLeft'}
			class:lg:col-end-7={content.align === 'imageLeft'}
		>
			{#if content.asset?.length > 0}
				<div class="aspect-4/3 overflow-hidden rounded-2xl">
					<Blok content={content.asset[0]} />
				</div>
			{:else if content.image?.filename}
				<Image
					class="aspect-4/3 rounded-xl object-cover max-lg:hidden"
					src={content.image.filename}
					title={content.image.title ?? ''}
					alt={content.image.alt ?? ''}
					width={1300}
				/>
			{/if}
		</div>
		<div
			class="flex flex-col justify-center max-lg:col-span-full max-lg:gap-y-4"
			class:lg:col-start-1={content.align === 'imageRight'}
			class:lg:col-end-6={content.align === 'imageRight'}
			class:lg:col-start-8={content.align === 'imageLeft'}
			class:lg:col-end-13={content.align === 'imageLeft'}
			class:text-center={content.text_align === 'center'}
			class:items-center={content.text_align === 'center'}
		>
			<div
				class="font-rhymes text-[68px]/[94%] tracking-[-2.04px] lg:text-[68px]/[100%] lg:tracking-[-1.36px]"
				class:lg:hidden={content.title_visibility === 'mobile_only'}
				class:max-lg:hidden={content.title_visibility === 'desktop_only' ||
					titlePosition === 'above_image'}
				class:text-[#FA7765]={content.color_variant === 'red'}
				class:text-[#79D397]={content.color_variant === 'green'}
				class:text-[#F0AA77]={content.color_variant === 'brown'}
				class:text-[#9CBCFF]={content.color_variant === 'blue'}
				class:text-white={[
					'red-gradient',
					'green-gradient',
					'brown-gradient',
					'blue-gradient'
				].includes(content.color_variant)}
				class:text-brown-1={content.color_variant === 'white'}
			>
				<SbRichtext text={content.title} />
			</div>
			<div
				class="text-[16px]/[150%] lg:mt-5 lg:text-[20px]/[140%]"
				class:text-white={content.color_variant && content.color_variant !== 'white'}
				class:text-brown-1={!content.color_variant || content.color_variant === 'white'}
			>
				<SbRichtext text={content.text} />
			</div>

			{#if content.cta_label}
				<div class="mt-7 flex lg:mt-18">
					{#if !content.color_variant || content.color_variant === 'white'}
						<ButtonBase
							size="large"
							href={getSbUrl(content.cta_target)}
							color="nyc-dark"
							class="font-normal {buttonTextColor}"
						>
							{content.cta_label || 'Get white-glove delivery'}</ButtonBase
						>
					{:else}
						<ButtonBase size="large" href={getSbUrl(content.cta_target)} color="nyc-white-blue">
							<span class="font-normal {buttonTextColor}"
								>{content.cta_label || 'Get white-glove delivery'}</span
							>
						</ButtonBase>
					{/if}
				</div>
			{/if}
		</div>
	</PageGrid>
</div>
